// hooks
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

// constants
import {
  EMPLOYEES_QUERY_KEY,
  OPEN_REQUESTS_QUERY_KEY
} from '../../RosterPlan.consts';

// types
import type {
  AutomaticRosteringDelivery,
  AutomaticRosteringState
} from '../automaticRostering.types';

// utils
import { mutations, queries } from 'RestAPIs/rapini';
import { isAutomaticRosteringOutcome } from '../automaticRostering.utils';

/** How often to ask the engine again while it is calculating. */
const POLL_INTERVAL_MS = 5_000;

/**
 * How many fresh `NOT_STARTED` answers - or failed reads - a run started here waits through before
 * giving up - a minute at the poll interval. The engine can take a moment to register a start, and a
 * status read the backend could not complete is reported as `NOT_STARTED` too, so one such answer
 * says nothing; a minute of them says the run is not coming.
 */
const MAX_NOT_STARTED_READS = 12;

type UseArRunProps = {
  organizationalUnitCode: string | null;
  /** Monday of the week on screen, as an ISO date (yyyy-MM-dd). */
  startDate: string;
  /** Sunday of that same week. */
  endDate: string;
  enabled: boolean;
};

/**
 * The automatic rostering calculation for the week the roster plan is showing.
 *
 * <p>The engine is the system of record for its own runs and SDB Planning stores nothing about them,
 * so the state comes from asking rather than from remembering: the status endpoint is read for the
 * unit and week on screen, which also means a calculation started yesterday is picked up today.
 *
 * <p>While the engine is calculating the status is re-read every few seconds, and each read carries
 * the step the engine is on. There is no percentage to show — the engine reports steps, not how far
 * along it is.
 */
const useAutomaticRosteringDelivery = ({
  organizationalUnitCode,
  startDate,
  endDate,
  enabled
}: UseArRunProps) => {
  const queryClient = useQueryClient();
  const [isStarting, setIsStarting] = useState(false);
  // Set when the planner starts a calculation here, cleared once it reaches a state it will not
  // leave. Keyed on the run rather than on the previous state: right after starting, the status query
  // has not answered yet and the state falls back to what it knew, so "was it in progress a render
  // ago" is false exactly when the delivery lands.
  const startedRunRef = useRef(false);
  // What the status query had last read when this run started. Anything it read before that says
  // nothing about the run, whatever value it carries.
  const statusReadAtStartRef = useRef(0);
  // Fresh `NOT_STARTED` answers and failed reads since this run started, in a row, counted against
  // MAX_NOT_STARTED_READS.
  const notStartedReadsRef = useRef(0);
  // When the status query had last failed as this run started; only failures after that count.
  const statusErrorAtStartRef = useRef(0);
  // The render-side twin of the refs above: what the status query had read when the run started
  // here, or null when no run started here is going. The refs drive the effect below; this drives
  // what the screen shows, which a ref cannot - changing one re-renders nothing.
  const [followedRunReadAt, setFollowedRunReadAt] = useState<number | null>(
    null
  );

  const canAsk = enabled && !!organizationalUnitCode;

  const {
    data: status,
    dataUpdatedAt,
    errorUpdatedAt,
    isLoading: isStatusPending
  } = queries.useGetAutomaticRosteringDeliveryStatus(
    organizationalUnitCode ?? '',
    startDate,
    endDate,
    {
      enabled: canAsk,
      // While the engine is busy, and for as long as a run started here has not ended: the first
      // answers after a start can still say NOT_STARTED, and stopping on those meant the delivery
      // was never read. A delivered or failed calculation does not change on its own.
      refetchInterval: (data) =>
        data?.status === 'CALCULATING' || startedRunRef.current
          ? POLL_INTERVAL_MS
          : false,
      // A tenant without automatic rostering answers 409 for every call. Retrying that is pointless
      // and would put three failures in the console per navigation.
      retry: false
    }
  );

  const startMutation = mutations.useStartAutomaticRosteringDelivery({
    // Nothing was handed to the engine, so there is no delivery to wait for and the flag has to go -
    // otherwise it sits there until the week changes and fires on someone else's calculation.
    onError: () => {
      startedRunRef.current = false;
      setFollowedRunReadAt(null);
    },
    onSettled: () => {
      setIsStarting(false);
      queryClient.invalidateQueries(['getAutomaticRosteringDeliveryStatus']);
    }
  });

  // What the engine said, as opposed to what the screen shows. The effect below judges the run on
  // this; the screen gets `state`.
  const engineState: AutomaticRosteringState = status?.status ?? 'idle';

  // A run started here stays `starting` until the engine has picked it up. Until the status has been
  // read again it holds the calculation from before - a failed one, whose steps would show this run
  // as failed on step two - or nothing at all, and the first reads after that can still say
  // NOT_STARTED. Shown as they are, all of them re-enable the generate button and close the
  // explanation panel it just opened.
  const isFollowingRun = followedRunReadAt !== null;
  const isAwaitingEngine =
    isFollowingRun &&
    (dataUpdatedAt === followedRunReadAt ||
      engineState === 'idle' ||
      engineState === 'NOT_STARTED');

  const state: AutomaticRosteringState =
    isStarting || isAwaitingEngine ? 'starting' : engineState;

  const run: AutomaticRosteringDelivery = {
    state,
    basePlanId: status?.basePlanId ?? undefined,
    message: status?.message ?? undefined,
    progressSteps: state === 'starting' ? [] : status?.progressSteps ?? []
  };

  // The engine writes its result into the roster itself, so the grid has to re-read rather than
  // patch what it has. These are the two queries the roster plan draws from.
  //
  // Only for a calculation started here: navigating to a week that was delivered earlier changes
  // nothing about what the grid already shows, and refetching it on every such navigation would cost
  // two roster reads for nothing.
  //
  // The backend reports calculating, creating the base plan and rolling it out as one run: the state
  // stays CALCULATING until the rollout has ended, so an outcome here means the roster is as it will
  // be. That outcome can be FAILED with part of the week already in the roster - a rollout commits
  // day by day - so every outcome re-reads, not only DELIVERED. It is one read per run.
  useEffect(() => {
    if (!startedRunRef.current) return;
    // `invalidateQueries` schedules a refetch, it does not clear what the query holds, so the render
    // right after the start settles still serves the answer from before the run. Reading that as this
    // run's outcome retires it one render too early, and the delivery a minute later then refreshes
    // nothing. Which value it carries does not matter - 'NOT_STARTED' on a fresh week, but just as
    // easily the 'FAILED' or 'DELIVERED' of an earlier attempt - so the test is whether the status has
    // been read again at all since the run started, not what it says.
    if (dataUpdatedAt === statusReadAtStartRef.current) return;
    // A fresh read can still be 'NOT_STARTED': the engine has the request but has not registered it
    // yet, or the read failed and the backend reported it as such. Only a state a calculation ends
    // in retires the run - or a minute of NOT_STARTED, after which it is not coming.
    if (engineState === 'NOT_STARTED') {
      notStartedReadsRef.current += 1;
      if (notStartedReadsRef.current >= MAX_NOT_STARTED_READS) {
        startedRunRef.current = false;
        setFollowedRunReadAt(null);
      }
      return;
    }
    // Only a minute of them in a row: a long run can see the odd failed read, and adding those up
    // would retire it before its outcome arrives - which then refreshes nothing.
    notStartedReadsRef.current = 0;
    if (!isAutomaticRosteringOutcome(engineState)) return;

    startedRunRef.current = false;
    setFollowedRunReadAt(null);

    queryClient.invalidateQueries([EMPLOYEES_QUERY_KEY]);
    queryClient.invalidateQueries([OPEN_REQUESTS_QUERY_KEY]);
  }, [engineState, dataUpdatedAt, queryClient]);

  // A read that fails between the browser and the backend - an expired session, a gateway error -
  // leaves the query holding what it had, so the effect above never sees a fresh answer and the run
  // would stay `starting` for as long as the screen is open. Counted with the NOT_STARTED answers:
  // either way the engine has not been heard from, and a minute of that ends the wait.
  useEffect(() => {
    if (!startedRunRef.current) return;
    if (errorUpdatedAt === statusErrorAtStartRef.current) return;

    notStartedReadsRef.current += 1;
    if (notStartedReadsRef.current >= MAX_NOT_STARTED_READS) {
      startedRunRef.current = false;
      setFollowedRunReadAt(null);
    }
  }, [errorUpdatedAt]);

  // A run belongs to the week it was started for; carrying the flag to another week would refresh
  // the wrong roster.
  useEffect(() => {
    startedRunRef.current = false;
    setFollowedRunReadAt(null);
  }, [organizationalUnitCode, startDate, endDate]);

  const startRun = () => {
    if (!organizationalUnitCode) return;

    startedRunRef.current = true;
    statusReadAtStartRef.current = dataUpdatedAt;
    statusErrorAtStartRef.current = errorUpdatedAt;
    notStartedReadsRef.current = 0;
    setFollowedRunReadAt(dataUpdatedAt);
    setIsStarting(true);
    startMutation.mutate({
      organizationalUnitCodes: [organizationalUnitCode],
      startDate,
      endDate,
      // The planner is looking at a roster of people, so a week of unassigned shifts is not what
      // they asked for. The flag defaults to false in the contract because the engine also serves
      // callers that only want the shifts; this caller is not one of them.
      assignEmployees: true,
      // A week that already carries a calculation is answered with it rather than recalculated, and
      // a failed or superseded one is still a calculation. Both are states the screen invites the
      // planner out of - the failure message sits in the generate tooltip, and the chat greets a
      // superseded week with "generate again to get an explanation" - so without this that next
      // click would hand back the very calculation being complained about and nothing would run.
      // Not on DELIVERED or MANUAL_REVIEW: there the existing calculation is the one in use.
      forceNew: engineState === 'FAILED' || engineState === 'SUPERSEDED'
    });
  };

  return {
    run,
    startRun,
    hasFailedToStart: startMutation.isError,
    // The first read of this unit and week is still out. `idle` alone cannot say so: it is also what
    // a status that is not asked for, or whose read failed, falls back to.
    isStatusPending
  };
};

export default useAutomaticRosteringDelivery;
